import axios, { AxiosRequestConfig } from "axios";
import { Debug } from "../../../../../utils/debug";
import { LockHolderData } from "../../../Company/LockHolderData";
import {
  UnlocKey,
  UnlocKeyEvent,
  UnlocLock,
  UnlocLockHolder,
  UnlocRequestCreateKey,
  UnlocRequestCreateLockHolder,
  UnlocRequestCreateVendorConnection,
  UnlocRequestGetKeys,
  UnlocRole,
  UnlocVendorConnection,
} from "./UnlocTypes";
import { IS_PRODUCTION } from "@src/index";

export class UnlocAPI {
  static async createLockholder(token: string, request: UnlocRequestCreateLockHolder): Promise<UnlocLockHolder> {
    const result = await axios.post(this.getBaseURL() + "/lock-holders/", request, this.getConfigFromToken(token));
    if (result.data.lockHolder) {
      return result.data.lockHolder as UnlocLockHolder;
    } else {
      throw new Error("UnlocAPI.createLockholder: ");
    }
  }

  static async updateLockholderName(lockHolder: LockHolderData, name: string): Promise<UnlocLockHolder> {
    const result = await axios.patch(this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId, { name: name }, this.getConfig(lockHolder));
    if (result.data.lockHolder) {
      return result.data.lockHolder as UnlocLockHolder;
    } else {
      Debug.logUnloc("UnlocAPI.updateLockholderName", lockHolder, undefined);
      throw new Error("UnlocAPI.updateLockholderName: ");
    }
  }

  static async getKeys(lockHolder: LockHolderData, request?: UnlocRequestGetKeys): Promise<UnlocKey[]> {
    let url = this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/keys/";
    if (request) url = UnlocAPI.addParamsToURL(url, request as { [key: string]: string });
    const result = await axios.get(url, this.getConfig(lockHolder));
    if (result.data.keys && Array.isArray(result.data.keys)) {
      return result.data.keys as UnlocKey[];
    } else {
      Debug.logUnloc("UnlocAPI.getKeys", lockHolder, result.data.error);
      throw new Error("UnlocAPI.getKeys: " + result.data.error);
    }
  }
  static async createKey(lockHolder: LockHolderData, request: UnlocRequestCreateKey): Promise<UnlocKey> {
    const result = await axios.post(this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/keys/", request, this.getConfig(lockHolder));
    if (result.data.key) {
      return result.data.key as UnlocKey;
    } else {
      Debug.logUnloc("UnlocAPI.createKey", lockHolder, result.data.error, request);
      throw new Error("UnlocAPI.createKey: " + result.data.error);
    }
  }
  static async revokeKey(lockHolder: LockHolderData, key: UnlocKey): Promise<UnlocKey> {
    const result = await axios.post(
      this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/keys/" + key.id + "/revoke/",
      undefined,
      this.getConfig(lockHolder)
    );
    if (result.data.key) {
      return result.data.key as UnlocKey;
    } else {
      Debug.logUnloc("UnlocAPI.revokeKey", lockHolder, result.data.error, key);
      throw new Error("UnlocAPI.revokeKey: " + result.data.error);
    }
  }

  static async getLocks(lockHolder: LockHolderData): Promise<UnlocLock[]> {
    let url = this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/locks/";

    const result = await axios.get(url, this.getConfig(lockHolder));
    if (result.data.locks && Array.isArray(result.data.locks)) {
      return result.data.locks as UnlocLock[];
    } else {
      Debug.logUnloc("UnlocAPI.getLocks", lockHolder, result.data.error);
      throw new Error("UnlocAPI.getLocks: " + result.data.error);
    }
  }

  static async updateLockName(lockHolder: LockHolderData, lock: UnlocLock, name: string): Promise<UnlocLock> {
    const result = await axios.patch(
      this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/locks/" + lock.id,
      { name: name },
      this.getConfig(lockHolder)
    );
    if (result.data.lock) {
      return result.data.lock as UnlocLock;
    } else {
      Debug.logUnloc("UnlocAPI.updateLockName", lockHolder, undefined, { name: name });
      throw new Error("UnlocAPI.updateLockName: ");
    }
  }

  static async updateLockImage(lockHolder: LockHolderData, lock: UnlocLock, image: string): Promise<UnlocLock> {
    const result = await axios.patch(
      this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/locks/" + lock.id,
      { image: image },
      this.getConfig(lockHolder)
    );
    if (result.data.lock) {
      return result.data.lock as UnlocLock;
    } else {
      Debug.logUnloc("UnlocAPI.updateLockName", lockHolder, undefined, { image: image });
      throw new Error("UnlocAPI.updateLockName: ");
    }
  }

  static async getRoles(lockHolder: LockHolderData): Promise<UnlocRole[]> {
    let url = this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/roles/";

    const result = await axios.get(url, this.getConfig(lockHolder));
    if (result.data.roles && Array.isArray(result.data.roles)) {
      return result.data.roles as UnlocRole[];
    } else {
      Debug.logUnloc("UnlocAPI.getRoles", lockHolder, result.data.error);
      throw new Error("UnlocAPI.getRoles: " + result.data.error);
    }
  }

  static async updateRole(lockHolder: LockHolderData, lock: string, user: string, canShare: boolean): Promise<UnlocRole> {
    const result = await axios.put(
      this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/locks/" + lock + "/roles/" + user,
      { canShare: canShare },
      this.getConfig(lockHolder)
    );
    if (result.data.role) {
      return result.data.role as UnlocRole;
    } else {
      Debug.logUnloc("UnlocAPI.updateRole", lockHolder, undefined);
      throw new Error("UnlocAPI.updateRole: ");
    }
  }

  static async createVendorConnection(lockHolder: LockHolderData, request: UnlocRequestCreateVendorConnection): Promise<UnlocVendorConnection> {
    const result = await axios.post(
      this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/vendor-connections/",
      request,
      this.getConfig(lockHolder)
    );
    if (result.data.vendorConnection) {
      return result.data.vendorConnection as UnlocVendorConnection;
    } else {
      Debug.logUnloc("UnlocAPI.createVendorConnection", lockHolder, result.data.error, request);
      throw new Error("UnlocAPI.createVendorConnection: " + result.data.error);
    }
  }
  static async getVendorConnections(lockHolder: LockHolderData): Promise<UnlocVendorConnection[]> {
    let url = this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/vendor-connections/";

    const result = await axios.get(url, this.getConfig(lockHolder));
    if (result.data.vendorConnections && Array.isArray(result.data.vendorConnections)) {
      return result.data.vendorConnections as UnlocVendorConnection[];
    } else {
      Debug.logUnloc("UnlocAPI.getVendorConnections", lockHolder, result.data.error);
      throw new Error("UnlocAPI.getVendorConnections: " + result.data.error);
    }
  }
  static async refreshVendorConnection(lockHolder: LockHolderData, vc: UnlocVendorConnection): Promise<void> {
    const result = await axios.put(
      this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/vendor-connections/" + vc.id + "/refresh/",
      undefined,
      this.getConfig(lockHolder)
    );
    if (result) {
      return;
    } else {
      Debug.logUnloc("UnlocAPI.refreshVendorConnection", lockHolder, undefined, vc);
      throw new Error("UnlocAPI.refreshVendorConnection: ");
    }
  }
  static async updateVendorPassword(lockHolder: LockHolderData, vc: string, password: string): Promise<void> {
    const result = await axios.put(
      this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/vendor-connections/" + vc + "/update-password/",
      { password: password },
      this.getConfig(lockHolder)
    );
    if (result) {
      return;
    } else {
      Debug.logUnloc("UnlocAPI.updateVendorPassword", lockHolder, undefined, vc);
      throw new Error("UnlocAPI.updateVendorPassword: ");
    }
  }

  static async getKeyEvents(lockHolder: LockHolderData, lock: UnlocLock): Promise<UnlocKeyEvent[]> {
    let url = this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/locks/" + lock.id + "/key-events/";

    const result = await axios.get(url, this.getConfig(lockHolder));
    if (result.data.events && Array.isArray(result.data.events)) {
      return result.data.events as UnlocKeyEvent[];
    } else {
      Debug.logUnloc("UnlocAPI.getKeyEvents", lockHolder, result.data.error);
      throw new Error("UnlocAPI.getKeyEvents: " + result.data.error);
    }
  }

  static async revokeSharedKeysForUser(lockHolder: LockHolderData, userId: string): Promise<number> {
    const result = await axios.delete(
      this.getBaseURL() + "/lock-holders/" + lockHolder.unlocId + "/users/" + userId + "/delete/",
      this.getConfig(lockHolder)
    );
    if (result.data) {
      return typeof result.data === "number" ? result.data : 0;
    } else {
      Debug.logUnloc("UnlocAPI.revokeSharedKeysForUser", lockHolder, undefined);
      throw new Error("UnlocAPI.revokeSharedKeysForUser: ");
    }
  }

  static addParamsToURL(url: string, params: { [key: string]: string }): string {
    let result = url;
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        if (params[key]) result += (result.indexOf("?") === -1 ? "?" : "&") + key + "=" + params[key];
      }
    }
    return result;
  }

  static getConfig(lockholder: LockHolderData): AxiosRequestConfig {
    return {
      headers: {
        Authorization: "Bearer " + lockholder.scopedAuth.data?.access_token,
        "Content-Type": "application/json",
      },
    };
  }
  static getConfigFromToken(token: string): AxiosRequestConfig {
    return {
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
    };
  }

  static getBaseURL(): string {
    const baseSandboxURL = "https://api-sandbox.unloc.app/integrators/v1";
    const baseProductionURL = "https://api.unloc.app/integrators/v1";

    return IS_PRODUCTION ? baseProductionURL : baseSandboxURL;
  }
}
